import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";

const HERO_IMG = "/media/principle-plst-studio.webp";

const linesTop = ["Strength", "changes", "lives."];
const linesSub = [
  "Physio-Led Strength Training combines the science of physiotherapy with the power of strength training.",
  "We understand where you are, where you want to go, and build the right program to help you get there.",
];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 1.1, ease: [0.2, 0.7, 0.2, 1], delay: 0.25 + i * 0.12 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.65]);

  return (
    <section ref={ref} className="relative pt-28 lg:pt-36 pb-20 lg:pb-28 overflow-hidden" data-testid="hero-section">
      {/* eyebrow */}
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-[#8A8A8A]"
        >
          <span className="w-8 h-px bg-[#A67C00]" />
          <span>Vile Parle | Thane | Andheri West</span>
        </motion.div>

        {/* Kinetic heading — italic, medium weight for editorial elegance */}
        <h1 className="mt-8 italic font-medium text-[13vw] sm:text-[10vw] md:text-[7.5vw] lg:text-[7vw] leading-[0.95] tracking-[-0.03em] text-[#1E1E1E]">
          {linesTop.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                variants={lineVariants}
                custom={i}
                initial="hidden"
                animate="show"
                className="block will-change-transform"
              >
                {i === 2 ? (
                  <span className="text-[#A67C00]">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        {/* Split lower deck */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="max-w-xl">
              {linesSub.map((line, i) => (
                <p key={i} className="block overflow-hidden">
                  <motion.span
                    variants={lineVariants}
                    custom={i + 3}
                    initial="hidden"
                    animate="show"
                    className="block text-base md:text-lg text-[#1E1E1E]/75 leading-relaxed mt-3 first:mt-0"
                  >
                    {line}
                  </motion.span>
                </p>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.7 }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/book"
                  data-testid="hero-book-cta"
                  className="group inline-flex items-center gap-3 bg-[#1E1E1E] text-[#F5F4EF] px-7 py-4 text-sm font-medium hover:bg-[#A67C00] transition-colors duration-500"
                >
                  Book an Assessment
                  <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero image with parallax + clipped frame */}
      <div className="relative mt-16 lg:mt-24 mx-auto max-w-[1600px] px-6 lg:px-12">
        <div className="relative aspect-[16/9] lg:aspect-[21/9] overflow-hidden border border-[#1E1E1E]/10">
          <motion.div style={{ y, scale }} className="absolute inset-0">
            <img
              src={HERO_IMG}
              alt="Architectural training studio"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </motion.div>
          <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#1E1E1E]" />
          <div className="absolute inset-0 flex items-end p-6 lg:p-12">
            <div className="text-[#F5F4EF] max-w-md">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#A67C00]">Our principle</div>
              <div className="mt-2 italic font-medium text-2xl lg:text-3xl leading-tight">
                "Safety comes from preparation, not protection."
              </div>
            </div>
          </div>
        </div>

        {/* stat strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-8 grid grid-cols-2 md:grid-cols-4 border-t border-[#1E1E1E]/10"
        >
          {[
            ["01", "Every client begins with a detailed assessment"],
            ["02", "Every program is personal — never a template"],
            ["03", "Led by physiotherapists who understand both movement and strength"],
            ["04", "Progress is measured, reviewed, and adjusted"],
          ].map(([num, txt]) => (
            <div key={num} className="py-6 pr-6 md:border-r last:border-r-0 border-[#1E1E1E]/10">
              <div className="italic font-medium text-3xl text-[#A67C00]">{num}</div>
              <div className="mt-2 text-sm text-[#1E1E1E]/75 leading-snug">{txt}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
